import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { toast } from "sonner";
import {
  Loader2, Plus, Upload, Search, Pencil, Trash2, X,
  AlertCircle, FileText, Download,
} from "lucide-react";

// ── Constants ──────────────────────────────────────────────────────────────

const BOARDS = ["AQA", "Edexcel", "OCR"];
const TIERS = ["Foundation", "Higher"];
const YEARS = [7, 8, 9, 10, 11];

const DIFF_BADGE = {
  1: { label: "Bronze",   cls: "bg-amber-100 text-amber-700 border-amber-200" },
  2: { label: "Silver",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
  3: { label: "Gold",     cls: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  4: { label: "Platinum", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  5: { label: "Diamond",  cls: "bg-purple-100 text-purple-700 border-purple-200" },
};

const CSV_TEMPLATE_HEADER =
  "board,tier,year,topic,subtopic,difficulty,question,option_a,option_b,option_c,option_d,correct,explanation,marks";

// ── Helpers ────────────────────────────────────────────────────────────────

function DiffBadge({ d }) {
  const b = DIFF_BADGE[d] || DIFF_BADGE[1];
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${b.cls}`}>
      {b.label}
    </span>
  );
}

function TypeBadge({ options }) {
  return options?.length > 0 ? (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-200">MCQ</span>
  ) : (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-teal-50 text-teal-600 border border-teal-200">Free-text</span>
  );
}

// ── StatsBar ──────────────────────────────────────────────────────────────

function StatsBar({ stats, loading }) {
  if (loading) return <div className="h-10 bg-white rounded-xl border border-slate-200 animate-pulse mb-6" />;
  const { total = 0, by_board = {}, by_tier = {} } = stats;
  return (
    <div className="flex flex-wrap items-center gap-2 mb-6">
      <span className="text-sm font-semibold text-slate-700">{total.toLocaleString()} questions</span>
      <span className="text-slate-300">·</span>
      {BOARDS.map(b => (
        <span key={b} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">
          {b}: {(by_board[b] || 0).toLocaleString()}
        </span>
      ))}
      <span className="text-slate-300">·</span>
      {TIERS.map(t => (
        <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-medium border border-amber-200">
          {t}: {(by_tier[t] || 0).toLocaleString()}
        </span>
      ))}
    </div>
  );
}

// ── QuestionModal (Add / Edit) ─────────────────────────────────────────────

const EMPTY_FORM = {
  board: "AQA", tier: "Foundation", year: 11, topic: "", subtopic: "",
  difficulty: 1, marks: 1, question: "", qtype: "free",
  optionA: "", optionB: "", optionC: "", optionD: "", correct: "", explanation: "",
};

function QuestionModal({ initial, topics, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => {
    if (!initial) return EMPTY_FORM;
    const isMcq = (initial.options || []).length > 0;
    return {
      board: initial.board || "AQA",
      tier: initial.tier || "Foundation",
      year: initial.year || 11,
      topic: initial.topic || "",
      subtopic: initial.subtopic || "",
      difficulty: initial.difficulty || 1,
      marks: initial.marks || 1,
      question: initial.question || "",
      qtype: isMcq ? "mcq" : "free",
      optionA: initial.options?.[0] || "",
      optionB: initial.options?.[1] || "",
      optionC: initial.options?.[2] || "",
      optionD: initial.options?.[3] || "",
      correct: initial.correct || "",
      explanation: initial.explanation || "",
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    if (!form.topic.trim()) return toast.error("Topic is required");
    if (!form.question.trim()) return toast.error("Question is required");
    if (!form.correct.trim()) return toast.error("Correct answer is required");

    const options =
      form.qtype === "mcq"
        ? [form.optionA, form.optionB, form.optionC, form.optionD].filter(Boolean)
        : [];

    if (form.qtype === "mcq" && options.length < 2)
      return toast.error("MCQ needs at least 2 options");
    if (form.qtype === "mcq" && !options.includes(form.correct))
      return toast.error("Correct answer must match one of the options exactly");

    const payload = {
      board: form.board, tier: form.tier, year: Number(form.year),
      topic: form.topic.trim(), subtopic: form.subtopic.trim() || null,
      difficulty: Number(form.difficulty), marks: Number(form.marks),
      question: form.question.trim(), options,
      correct: form.correct.trim(), explanation: form.explanation.trim(),
    };

    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/questions/${initial.id}`, payload);
        toast.success("Question updated");
      } else {
        await api.post("/admin/questions", payload);
        toast.success("Question created");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="font-semibold text-slate-800">{isEdit ? "Edit Question" : "Add Question"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Board / Tier / Year */}
          <div className="grid grid-cols-3 gap-3">
            {[
              ["Board", "board", BOARDS],
              ["Tier", "tier", TIERS],
              ["Year", "year", YEARS],
            ].map(([label, key, opts]) => (
              <div key={key}>
                <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
                <select
                  value={form[key]}
                  onChange={e => set(key, key === "year" ? Number(e.target.value) : e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                >
                  {opts.map(o => (
                    <option key={o} value={o}>
                      {key === "year" ? `Year ${o}` : o}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {/* Topic / Subtopic */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Topic *</label>
              <input
                list="topics-list" value={form.topic} onChange={e => set("topic", e.target.value)}
                placeholder="e.g. Algebra"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
              <datalist id="topics-list">
                {topics.map(t => <option key={t} value={t} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Subtopic</label>
              <input
                value={form.subtopic} onChange={e => set("subtopic", e.target.value)}
                placeholder="Optional"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
            </div>
          </div>

          {/* Difficulty / Marks */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Difficulty</label>
              <select
                value={form.difficulty} onChange={e => set("difficulty", Number(e.target.value))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              >
                {[1,2,3,4,5].map(n => (
                  <option key={n} value={n}>{n} — {DIFF_BADGE[n].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Marks</label>
              <input
                type="number" min={1} max={10} value={form.marks}
                onChange={e => set("marks", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
            </div>
          </div>

          {/* Question type */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Question type</label>
            <div className="flex gap-5">
              {[["free", "Free-text"], ["mcq", "Multiple Choice (MCQ)"]].map(([val, lbl]) => (
                <label key={val} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio" name="qtype" value={val}
                    checked={form.qtype === val} onChange={() => set("qtype", val)}
                  />
                  {lbl}
                </label>
              ))}
            </div>
          </div>

          {/* Question text */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Question *</label>
            <textarea
              rows={4} value={form.question} onChange={e => set("question", e.target.value)}
              placeholder="Enter the question text…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
            />
          </div>

          {/* MCQ options */}
          {form.qtype === "mcq" && (
            <div className="grid grid-cols-2 gap-3">
              {[["optionA","A"],["optionB","B"],["optionC","C"],["optionD","D"]].map(([key, lbl]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Option {lbl}</label>
                  <input
                    value={form[key]} onChange={e => set(key, e.target.value)}
                    placeholder={`Option ${lbl}`}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Correct answer */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Correct answer *{" "}
              <span className="text-slate-400 font-normal">
                {form.qtype === "mcq" ? "(must match an option exactly)" : "(exact value or expression)"}
              </span>
            </label>
            {form.qtype === "mcq" ? (
              <select
                value={form.correct} onChange={e => set("correct", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              >
                <option value="">— select correct option —</option>
                {[form.optionA, form.optionB, form.optionC, form.optionD]
                  .filter(Boolean)
                  .map((o, i) => <option key={i} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                value={form.correct} onChange={e => set("correct", e.target.value)}
                placeholder="e.g. 42  or  x = 3"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
            )}
          </div>

          {/* Explanation */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Explanation</label>
            <textarea
              rows={3} value={form.explanation} onChange={e => set("explanation", e.target.value)}
              placeholder="Step-by-step solution shown to students after submission…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 border border-slate-200 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={handleSave} disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-[#0A1128] text-white hover:bg-[#0A1128]/90 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? "Save changes" : "Add question"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DeleteConfirmModal ─────────────────────────────────────────────────────

function DeleteConfirmModal({ question, onClose, onDeleted }) {
  const [loading, setLoading] = useState(false);

  async function handleDelete() {
    setLoading(true);
    try {
      await api.delete(`/admin/questions/${question.id}`);
      toast.success("Question deleted");
      onDeleted();
      onClose();
    } catch {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-slate-800 mb-1">Delete question?</h3>
            <p className="text-sm text-slate-500 line-clamp-3">{question.question}</p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 border border-slate-200 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={handleDelete} disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading && <Loader2 size={13} className="animate-spin" />} Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ImportModal ────────────────────────────────────────────────────────────

function ImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  function downloadTemplate() {
    const sample =
      CSV_TEMPLATE_HEADER +
      "\nAQA,Foundation,11,Algebra,,1,Solve x + 3 = 7,,,,,4,Subtract 3 from both sides: x = 4,1" +
      "\nAQA,Foundation,10,Number,,2,Which is the largest?,3.14,22/7,3.142,3.1416,3.142,22/7 ≈ 3.1428 is largest,1";
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "questions_template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  async function handleUpload() {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/admin/questions/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`${res.data.imported} questions imported`);
        onImported();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">Import Questions (CSV)</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-1">
            <div className="text-xs font-semibold text-slate-700 mb-1">Required columns:</div>
            <div className="font-mono text-[10px] text-slate-500 break-all">{CSV_TEMPLATE_HEADER}</div>
            <div className="text-xs text-slate-400 mt-2">
              Leave option_a–option_d blank for free-text questions.
              Board: AQA / Edexcel / OCR · Tier: Foundation / Higher
            </div>
          </div>

          <button onClick={downloadTemplate} className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 font-medium">
            <Download size={13} /> Download template CSV
          </button>

          <div
            className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-amber-300 transition-colors"
            onClick={() => inputRef.current?.click()}
          >
            <FileText size={28} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">{file ? file.name : "Click to select CSV file"}</div>
            <input
              ref={inputRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={e => { setFile(e.target.files[0]); setResult(null); }}
            />
          </div>

          {result && (
            <div className={`rounded-xl p-4 text-sm border ${result.errors?.length ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}>
              <div className="font-semibold mb-1">
                {result.imported > 0 && <span className="text-green-700">{result.imported} imported </span>}
                {result.skipped > 0 && <span className="text-amber-700">{result.skipped} skipped</span>}
              </div>
              {result.errors?.slice(0, 5).map((e, i) => (
                <div key={i} className="text-xs text-red-600">Row {e.row}: {e.reason}</div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 border border-slate-200 hover:bg-slate-50">
            Close
          </button>
          <button
            onClick={handleUpload} disabled={!file || uploading}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-[#0A1128] text-white hover:bg-[#0A1128]/90 disabled:opacity-50 flex items-center gap-2"
          >
            {uploading && <Loader2 size={14} className="animate-spin" />} Import
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Question Row ───────────────────────────────────────────────────────────

function QuestionRow({ q, onEdit, onDelete }) {
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors group">
      <td className="py-3 px-4 max-w-[380px]">
        <div className="text-sm text-slate-800 line-clamp-2">{q.question}</div>
        {q.subtopic && <div className="text-xs text-slate-400 mt-0.5">{q.subtopic}</div>}
      </td>
      <td className="py-3 px-3 text-xs text-slate-600 whitespace-nowrap">{q.topic}</td>
      <td className="py-3 px-3 text-center">
        <span className="text-xs font-medium text-slate-500">Yr {q.year}</span>
      </td>
      <td className="py-3 px-3 text-center">
        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{q.board}</span>
      </td>
      <td className="py-3 px-3 text-center">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${q.tier === "Higher" ? "bg-purple-50 text-purple-700" : "bg-sky-50 text-sky-700"}`}>
          {q.tier}
        </span>
      </td>
      <td className="py-3 px-3 text-center"><DiffBadge d={q.difficulty} /></td>
      <td className="py-3 px-3 text-center"><TypeBadge options={q.options} /></td>
      <td className="py-3 px-3 text-center text-xs text-slate-400">{q.marks}m</td>
      <td className="py-3 px-3 text-right">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(q)}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800"
            title="Edit"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={() => onDelete(q)}
            className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-600"
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function AdminQuestions() {
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [shellStats, setShellStats] = useState({});
  const [topics, setTopics] = useState([]);

  // Filters
  const [search, setSearch] = useState("");
  const [board, setBoard] = useState("");
  const [tier, setTier] = useState("");
  const [year, setYear] = useState("");
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [skip, setSkip] = useState(0);
  const LIMIT = 100;

  // Modals
  const [addOpen, setAddOpen] = useState(false);
  const [editQ, setEditQ] = useState(null);
  const [deleteQ, setDeleteQ] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const searchTimer = useRef(null);

  const fetchStats = useCallback(() => {
    setStatsLoading(true);
    api.get("/admin/questions/stats")
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []);

  const fetchQuestions = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: LIMIT, skip });
    if (search) params.set("search", search);
    if (board) params.set("board", board);
    if (tier) params.set("tier", tier);
    if (year) params.set("year", year);
    if (topic) params.set("topic", topic);
    if (difficulty) params.set("difficulty", difficulty);
    api.get(`/admin/questions?${params}`)
      .then(r => { setQuestions(r.data.questions || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error("Failed to load questions"))
      .finally(() => setLoading(false));
  }, [search, board, tier, year, topic, difficulty, skip]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    api.get("/admin/stats").then(r => setShellStats(r.data)).catch(() => {});
    api.get("/questions/topics").then(r => setTopics(r.data.topics || [])).catch(() => {});
  }, []);
  useEffect(() => { setSkip(0); }, [search, board, tier, year, topic, difficulty]);
  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  function handleSearchChange(val) {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(val), 300);
  }

  function handleSaved() { fetchStats(); fetchQuestions(); }

  const hasFilter = search || board || tier || year || topic || difficulty;

  return (
    <AdminShell title="Question Bank" subtitle="Manage and search all GCSE maths questions" stats={shellStats}>
      <StatsBar stats={stats} loading={statsLoading} />

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text" placeholder="Search questions…"
            onChange={e => handleSearchChange(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        {[
          ["Board", board, setBoard, BOARDS],
          ["Tier", tier, setTier, TIERS],
        ].map(([label, val, setter, opts]) => (
          <select key={label} value={val} onChange={e => setter(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white text-slate-700">
            <option value="">{label}</option>
            {opts.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}

        <select value={year} onChange={e => setYear(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white text-slate-700">
          <option value="">Year</option>
          {YEARS.map(y => <option key={y} value={y}>Year {y}</option>)}
        </select>

        <select value={difficulty} onChange={e => setDifficulty(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white text-slate-700">
          <option value="">Difficulty</option>
          {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} — {DIFF_BADGE[n].label}</option>)}
        </select>

        <div>
          <input
            list="filter-topics-list" value={topic} onChange={e => setTopic(e.target.value)}
            placeholder="Topic…"
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 w-36"
          />
          <datalist id="filter-topics-list">
            {topics.map(t => <option key={t} value={t} />)}
          </datalist>
        </div>

        {hasFilter && (
          <button
            onClick={() => { setSearch(""); setBoard(""); setTier(""); setYear(""); setTopic(""); setDifficulty(""); }}
            className="text-xs text-slate-400 hover:text-slate-700 flex items-center gap-1"
          >
            <X size={12} /> Clear
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
          >
            <Upload size={14} /> Import CSV
          </button>
          <button
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0A1128] text-white rounded-lg hover:bg-[#0A1128]/90"
          >
            <Plus size={14} /> Add Question
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={26} className="animate-spin text-slate-300" />
          </div>
        ) : questions.length === 0 ? (
          <div className="py-20 text-center">
            <FileText size={32} className="mx-auto text-slate-200 mb-3" />
            <div className="text-slate-500 font-medium">
              {hasFilter
                ? "No questions match these filters — try broadening your search"
                : "No questions yet — add one or import a CSV"}
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Question</th>
                    <th className="text-left py-3 px-3 font-medium text-slate-500">Topic</th>
                    <th className="text-center py-3 px-3 font-medium text-slate-500">Year</th>
                    <th className="text-center py-3 px-3 font-medium text-slate-500">Board</th>
                    <th className="text-center py-3 px-3 font-medium text-slate-500">Tier</th>
                    <th className="text-center py-3 px-3 font-medium text-slate-500">Diff</th>
                    <th className="text-center py-3 px-3 font-medium text-slate-500">Type</th>
                    <th className="text-center py-3 px-3 font-medium text-slate-500">Marks</th>
                    <th className="py-3 px-3" />
                  </tr>
                </thead>
                <tbody>
                  {questions.map(q => (
                    <QuestionRow key={q.id} q={q} onEdit={setEditQ} onDelete={setDeleteQ} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
              <div className="text-xs text-slate-500">
                Showing {skip + 1}–{Math.min(skip + LIMIT, total)} of {total.toLocaleString()}
                {hasFilter ? " (filtered)" : ""}
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={skip === 0} onClick={() => setSkip(s => Math.max(0, s - LIMIT))}
                  className="px-3 py-1.5 text-xs rounded-md border border-slate-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  disabled={skip + LIMIT >= total} onClick={() => setSkip(s => s + LIMIT)}
                  className="px-3 py-1.5 text-xs rounded-md border border-slate-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {addOpen && (
        <QuestionModal topics={topics} onClose={() => setAddOpen(false)} onSaved={handleSaved} />
      )}
      {editQ && (
        <QuestionModal initial={editQ} topics={topics} onClose={() => setEditQ(null)} onSaved={handleSaved} />
      )}
      {deleteQ && (
        <DeleteConfirmModal question={deleteQ} onClose={() => setDeleteQ(null)} onDeleted={handleSaved} />
      )}
      {importOpen && (
        <ImportModal onClose={() => setImportOpen(false)} onImported={handleSaved} />
      )}
    </AdminShell>
  );
}
